import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { NavigationSection } from '@/types/navigation';
import { navigationService } from '@/services/navigation.service';
import { useAuth } from '@/hooks/useAuth';

interface NavigationContextType {
  sections: NavigationSection[];
  /** UIDs of nav sections permitted for the current user — used by useModuleConfig to filter the module switcher */
  enabledModuleKeys: string[];
  loading: boolean;
  error: Error | null;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined
);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [sections, setSections] = useState<NavigationSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasLoadedRef = useRef(false);

  const performLoadNavigation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await navigationService.getNavigation();
      setSections(data);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load navigation');
      setError(error);
      console.error('Failed to load navigation:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // Reset ref when user logs out
      hasLoadedRef.current = false;
      setSections([]);
      setError(null);
    } else {
      // Load navigation only once when authenticated
      if (!hasLoadedRef.current) {
        hasLoadedRef.current = true;
        performLoadNavigation();
      }
    }
  }, [isAuthenticated, performLoadNavigation]);

  const enabledModuleKeys = sections.map((s) => s.uid);

  const value: NavigationContextType = {
    sections,
    enabledModuleKeys,
    loading,
    error,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};
